import { CopyError } from '../copy/text.js';
import { viewerStrings } from '../copy/strings.js';
import type { DatasetDescriptor, ImportProgress } from '@cdr-clarity/contracts';
import type { WorkerClient } from '../runtime/worker-client.js';
import type { Workspace, importWorkspace } from './import-workspace.js';
import { InvalidViewPreferenceError, type PreferenceStorage } from './view-preferences.js';

interface ImportSnapshot {
  readonly accepted: DatasetDescriptor | null;
  readonly workspace: Workspace | null;
  readonly busy: boolean;
  readonly stage: 'idle' | 'import' | 'setup';
  readonly progress: ImportProgress | null;
  readonly error: string;
  readonly corruptPreferenceKey: string | null;
}

/** Owns the accepted worker session separately from the workspace that presents it. */
export class ImportController {
  private snapshot: ImportSnapshot = {
    accepted: null,
    workspace: null,
    busy: false,
    stage: 'idle',
    progress: null,
    error: '',
    corruptPreferenceKey: null,
  };
  private readonly listeners = new Set<() => void>();
  private corruptSerializedValue: string | null = null;
  constructor(
    private readonly client: WorkerClient,
    private readonly storage: PreferenceStorage,
    private readonly compose: typeof importWorkspace,
  ) {}
  getSnapshot = (): ImportSnapshot => this.snapshot;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };
  private publish(patch: Partial<ImportSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }
  private start(stage: 'import' | 'setup'): void {
    if (this.snapshot.busy)
      throw new CopyError(
        viewerStrings.webStateImportController.waitForTheCurrentImportOrWorkspace,
      );
    this.publish({ busy: true, stage, error: '', progress: null, corruptPreferenceKey: null });
  }
  private fail(reason: unknown): void {
    this.corruptSerializedValue =
      reason instanceof InvalidViewPreferenceError ? reason.serializedValue : null;
    this.publish({
      error: reason instanceof Error ? reason.message : String(reason),
      corruptPreferenceKey: reason instanceof InvalidViewPreferenceError ? reason.storageKey : null,
    });
  }
  private async setup(): Promise<void> {
    // Earlier queued operations may have advanced classification while import finished.
    const descriptor = await this.client.request({ operation: 'describe' });
    this.publish({ accepted: descriptor, stage: 'setup' });
    const next = await this.compose(this.client, descriptor, this.storage);
    this.snapshot.workspace?.unified?.dispose();
    this.snapshot.workspace?.records.dispose();
    this.publish({ workspace: next });
    await next.rules.restore();
  }
  async load(files: Blob[]): Promise<void> {
    this.start('import');
    try {
      const descriptor = await this.client.request(
        {
          operation: 'import',
          files,
          mode: this.snapshot.accepted ? 'append' : 'replace',
          sourceInstance: 'local-source',
          declaredRelease: null,
        },
        (progress) => this.publish({ progress }),
      );
      // Import committed. The old workspace is now stale even if subsequent setup fails.
      this.snapshot.workspace?.unified?.dispose();
      this.snapshot.workspace?.records.dispose();
      this.publish({ accepted: descriptor, workspace: null, stage: 'setup' });
      await this.setup();
    } catch (reason) {
      this.fail(reason);
    } finally {
      this.publish({ busy: false });
    }
  }
  async retrySetup(): Promise<void> {
    if (!this.snapshot.accepted)
      throw new CopyError(
        viewerStrings.webStateImportController.loadFilesBeforeRetryingWorkspaceSetup,
      );
    this.start('setup');
    try {
      await this.setup();
    } catch (reason) {
      this.fail(reason);
    } finally {
      this.publish({ busy: false });
    }
  }
  async resetCorruptPreference(): Promise<void> {
    if (this.snapshot.busy)
      throw new CopyError(
        viewerStrings.webStateImportController.waitForTheCurrentImportOrWorkspace2,
      );
    const key = this.snapshot.corruptPreferenceKey;
    if (key === null)
      throw new CopyError(
        viewerStrings.webStateImportController.noCorruptPresentationPreferenceHasBeenIdentified,
      );
    try {
      // A different tab may already have repaired the preference since the failure.
      if (this.storage.getItem(key) === this.corruptSerializedValue) this.storage.removeItem(key);
    } catch (reason) {
      this.fail(reason);
      return;
    }
    await this.retrySetup();
  }
  dispose(): void {
    this.snapshot.workspace?.unified?.dispose();
    this.snapshot.workspace?.records.dispose();
    this.listeners.clear();
  }
}
